import { useState, useEffect, useRef } from "react";
import Dialog from "@mui/material/Dialog";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import {
  Close as CloseIcon,
  Info as InfoIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  RotateRight as RotateRightIcon,
  Fullscreen as FullscreenIcon,
} from "@mui/icons-material";
import { ViewerInfoDrawer } from "./ViewerInfoDrawer.jsx";
import { PDFPinMarker } from "./PDFPinMarker.jsx";
import PdfViewer from "../../components/myDrawingSection/PdfViewer.js";
import { toast } from "react-hot-toast";
import {
  clampZoom,
  ZOOM_CONFIG,
  calculateFitPageZoom,
  calculateFitWidthZoom,
} from "../../config/zoom.config.ts";

export function FullscreenFileViewer({
  file,
  onClose,
  filesInFolder = [],
  onNavigateToFile,
  currentSession,
  viewedSession,
  recordings = [],
  onDeletePin,
  onUpdatePinLabel,
  onUpdatePinPosition,
  onRecordingClick,
  onRecordingPlay,
}) {
  const [zoom, setZoom] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const [rotation, setRotation] = useState(0);

  // Reset current page when file changes
  useEffect(() => {
    setCurrentPage(1);
  }, [file?.id]);
  const [infoDrawerOpen, setInfoDrawerOpen] = useState(false);
  const [pins, setPins] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [blobUrl, setBlobUrl] = useState(null);
  const [pdfPageWidth, setPdfPageWidth] = useState(0);
  const [pdfPageHeight, setPdfPageHeight] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const pdfContainerRef = useRef(null);
  const [spacebarPressed, setSpacebarPressed] = useState(false);
  const pdfViewerRef = useRef(null);
  const isPDF =
    file?.fileType?.toLowerCase().includes("pdf") ||
    file?.name.toLowerCase().endsWith(".pdf");
  const isImage =
    file?.fileType?.toLowerCase().includes("image") ||
    file?.name.toLowerCase().endsWith(".png") ||
    file?.name.toLowerCase().endsWith(".jpg") ||
    file?.name.toLowerCase().endsWith(".jpeg");

  // Create blob URL for PDF to prevent download dialog
  // Note: PdfViewer component will handle document loading and provide dimensions via onDocumentLoad
  useEffect(() => {
    if (!file?.fileUrl || !isPDF) {
      setBlobUrl(null);
      setTotalPages(0);
      setPdfPageWidth(0);
      setPdfPageHeight(0);
      return;
    }
    setIsLoading(true);
    setTotalPages(0);
    setPdfPageWidth(0);
    setPdfPageHeight(0);

    // Fetch the PDF and create a blob URL with proper headers to prevent download
    fetch(file.fileUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to fetch PDF");
        }
        return response.blob();
      })
      .then((blob) => {
        // Create a new blob with explicit type to ensure browser treats it as viewable
        const pdfBlob = new Blob([blob], {
          type: "application/pdf",
        });
        const url = URL.createObjectURL(pdfBlob);
        setBlobUrl(url);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error loading PDF:", error);
        setIsLoading(false);
        toast.error("Failed to load PDF");
      });

    // Cleanup blob URL when component unmounts or file changes
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [file?.fileUrl, isPDF]);

  // Handle document load from PdfViewer component
  const handleDocumentLoad = (docInfo) => {
    setTotalPages(docInfo.totalPages);
    setPdfPageWidth(docInfo.pageWidth);
    setPdfPageHeight(docInfo.pageHeight);
    // Reset current page if it exceeds total pages
    setCurrentPage((prev) => (prev > docInfo.totalPages ? 1 : prev));
    setIsLoading(false);
  };

  // Load pins from global storage for this drawing
  useEffect(() => {
    if (!file) return;
    try {
      const storedPins = localStorage.getItem("drawing-pins-global");
      if (storedPins) {
        const allPins = JSON.parse(storedPins);
        // Filter pins for this specific drawing and viewedSession
        const drawingPins = allPins.filter((pin) => {
          if (pin.drawingId !== file.id) return false;
          if (viewedSession !== null && viewedSession !== undefined) {
            return pin.sessionNumber === viewedSession;
          }
          return pin.sessionNumber === currentSession;
        });
        setPins(drawingPins);
      }
    } catch (error) {
      console.error("Error loading pins:", error);
    }
  }, [file, viewedSession, currentSession]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!file) return;

      // ESC to close
      if (e.key === "Escape") {
        onClose();
        return;
      }

      // Spacebar for temporary pan mode
      if (e.code === "Space" && !spacebarPressed) {
        e.preventDefault();
        setSpacebarPressed(true);
      }

      // Ctrl/Cmd + 0: Zoom to fit
      if ((e.ctrlKey || e.metaKey) && e.key === "0") {
        e.preventDefault();
        handleZoomToFit();
      }

      // Ctrl/Cmd + 1: Actual size
      if ((e.ctrlKey || e.metaKey) && e.key === "1") {
        e.preventDefault();
        handleActualSize();
      }

      // Ctrl/Cmd + Plus: Zoom in
      if ((e.ctrlKey || e.metaKey) && (e.key === "+" || e.key === "=")) {
        e.preventDefault();
        handleZoomIn();
      }

      // Ctrl/Cmd + Minus: Zoom out
      if ((e.ctrlKey || e.metaKey) && (e.key === "-" || e.key === "_")) {
        e.preventDefault();
        handleZoomOut();
      }

      // Fit Width (W)
      if (isPDF && e.key.toLowerCase() === "w" && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        handleFitToWidth();
      }

      // Fit Page (P)
      if (isPDF && e.key.toLowerCase() === "p" && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        handleZoomToFit();
      }

      // Arrow keys for file navigation
      if (
        onNavigateToFile &&
        filesInFolder.length > 0 &&
        !e.ctrlKey &&
        !e.metaKey
      ) {
        const currentIndex = filesInFolder.findIndex((f) => f.id === file.id);
        if (e.key === "ArrowLeft" && currentIndex > 0) {
          e.preventDefault();
          const prevFile = filesInFolder[currentIndex - 1];
          if (prevFile.type === "file") {
            onNavigateToFile(prevFile);
          }
        } else if (
          e.key === "ArrowRight" &&
          currentIndex < filesInFolder.length - 1
        ) {
          e.preventDefault();
          const nextFile = filesInFolder[currentIndex + 1];
          if (nextFile.type === "file") {
            onNavigateToFile(nextFile);
          }
        }
      }

      // Home/End for first/last page (PDF only)
      if (isPDF) {
        if (e.key === "Home") {
          e.preventDefault();
          setCurrentPage(1);
        } else if (e.key === "End") {
          e.preventDefault();
          setCurrentPage(totalPages);
        }
      }
    };
    const handleKeyUp = (e) => {
      // Release spacebar pan mode
      if (e.code === "Space") {
        setSpacebarPressed(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [
    file,
    filesInFolder,
    onNavigateToFile,
    isPDF,
    totalPages,
    spacebarPressed,
    zoom,
  ]);

  // Mouse wheel zoom support
  useEffect(() => {
    if (!isPDF || !pdfContainerRef.current) return;

    let wheelTimeout = null;
    const handleWheel = (e) => {
      // Only handle wheel zoom when Ctrl/Cmd is pressed
      if (!e.ctrlKey && !e.metaKey) return;

      e.preventDefault();

      // Clear existing timeout
      if (wheelTimeout) {
        clearTimeout(wheelTimeout);
      }

      // Debounce rapid wheel events
      wheelTimeout = setTimeout(() => {
        const delta =
          e.deltaY > 0
            ? -ZOOM_CONFIG.MOUSE_WHEEL_ZOOM_DELTA
            : ZOOM_CONFIG.MOUSE_WHEEL_ZOOM_DELTA;
        const newZoom = clampZoom(zoom + delta);

        if (pdfViewerRef.current && pdfViewerRef.current.zoomTo) {
          pdfViewerRef.current.zoomTo(newZoom);
        }
        setZoom(newZoom);
      }, ZOOM_CONFIG.MOUSE_WHEEL_DEBOUNCE);
    };

    const container = pdfContainerRef.current;
    container.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      container.removeEventListener("wheel", handleWheel);
      if (wheelTimeout) {
        clearTimeout(wheelTimeout);
      }
    };
  }, [isPDF, zoom]);
  const handleZoomIn = () => {
    const newZoom = clampZoom(zoom + ZOOM_CONFIG.ZOOM_INCREMENT_COARSE);
    if (pdfViewerRef.current && pdfViewerRef.current.zoomTo) {
      pdfViewerRef.current.zoomTo(newZoom);
    }
    setZoom(newZoom);
  };
  const handleZoomOut = () => {
    const newZoom = clampZoom(zoom - ZOOM_CONFIG.ZOOM_INCREMENT_COARSE);
    if (pdfViewerRef.current && pdfViewerRef.current.zoomTo) {
      pdfViewerRef.current.zoomTo(newZoom);
    }
    setZoom(newZoom);
  };
  const handleZoomReset = () => {
    if (pdfViewerRef.current && pdfViewerRef.current.zoomTo) {
      pdfViewerRef.current.zoomTo(100);
    }
    setZoom(100);
  };
  const handleZoomToFit = () => {
    if (pdfContainerRef.current && pdfPageWidth > 0 && pdfPageHeight > 0) {
      const container = pdfContainerRef.current;
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;
      const newZoom = calculateFitPageZoom(
        containerWidth,
        containerHeight,
        pdfPageWidth,
        pdfPageHeight,
        6 // padding
      );
      if (pdfViewerRef.current && pdfViewerRef.current.zoomTo) {
        pdfViewerRef.current.zoomTo(newZoom);
      }
      setZoom(newZoom);
    } else {
      if (pdfViewerRef.current && pdfViewerRef.current.zoomTo) {
        pdfViewerRef.current.zoomTo(100);
      }
      setZoom(100);
    }
  };
  const handleActualSize = () => {
    if (pdfViewerRef.current && pdfViewerRef.current.zoomTo) {
      pdfViewerRef.current.zoomTo(100);
    }
    setZoom(100);
  };
  const handleFitToWidth = () => {
    if (pdfContainerRef.current && pdfPageWidth > 0) {
      const container = pdfContainerRef.current;
      const containerWidth = container.clientWidth;
      const newZoom = calculateFitWidthZoom(containerWidth, pdfPageWidth, 6);
      if (pdfViewerRef.current && pdfViewerRef.current.zoomTo) {
        pdfViewerRef.current.zoomTo(newZoom);
      }
      setZoom(newZoom);
    }
  };
  const handleNextPage = () => {
    const nextPage = Math.min(currentPage + 1, totalPages);
    setCurrentPage(nextPage);
    if (pdfViewerRef.current && pdfViewerRef.current.jumpToPage) {
      pdfViewerRef.current.jumpToPage(nextPage);
    }
  };
  const handlePreviousPage = () => {
    const prevPage = Math.max(currentPage - 1, 1);
    setCurrentPage(prevPage);
    if (pdfViewerRef.current && pdfViewerRef.current.jumpToPage) {
      pdfViewerRef.current.jumpToPage(prevPage);
    }
  };
  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360);
  };
  const handleFullscreen = () => {
    // Use CSS-based fullscreen instead of Fullscreen API to avoid permissions policy errors
    setIsFullscreen(!isFullscreen);
    if (!isFullscreen) {
      toast.success("Fullscreen mode enabled");
    } else {
      toast.success("Fullscreen mode disabled");
    }
  };
  if (!file) return null;
  return (
    <Dialog
      open={!!file}
      onClose={onClose}
      maxWidth={false}
      fullScreen
      sx={{
        "& .MuiDialog-paper": {
          bgcolor: "#F2F2F2",
          margin: 0,
        },
      }}
      // Close on backdrop click
      slotProps={{
        backdrop: {
          onClick: onClose,
          sx: {
            bgcolor: "rgba(0, 0, 0, 0.85)", // Dark backdrop for focus
          },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          bgcolor: "#F2F2F2",
        }}
      >
        {/* Simple Header for Non-PDF Files - Hide in fullscreen mode */}
        {!isPDF && !isFullscreen && (
          <Box
            sx={{
              height: "40px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 1,
              gap: 1,
              borderBottom: "1px solid #E5E7EB",
              bgcolor: "#F2F2F2",
              flexShrink: 0,
            }}
          >
            {/* Left Section: Close + Filename */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                minWidth: 0,
                flex: "0 1 auto",
              }}
            >
              <Tooltip title="Close">
                <IconButton
                  onClick={onClose}
                  size="small"
                  sx={{
                    color: "#4A5565",
                    "&:hover": {
                      bgcolor: "#E5E7EB",
                    },
                    padding: "4px",
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Typography
                sx={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "13px",
                  fontWeight: 500,
                  color: "#212121",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: {
                    xs: "120px",
                    sm: "200px",
                    md: "300px",
                  },
                }}
              >
                {file.name}
              </Typography>
            </Box>

            {/* Center Section: Zoom Controls */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                flex: "0 0 auto",
              }}
            >
              <Tooltip title="Zoom out">
                <span>
                  <IconButton
                    onClick={handleZoomOut}
                    disabled={zoom <= 25}
                    size="small"
                    sx={{
                      color: "#4A5565",
                      "&:hover": {
                        bgcolor: "#E5E7EB",
                      },
                      "&:disabled": {
                        color: "#E5E7EB",
                      },
                    }}
                  >
                    <ZoomOutIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>

              <Typography
                onClick={handleZoomReset}
                sx={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "13px",
                  color: "#4A5565",
                  minWidth: {
                    xs: "35px",
                    sm: "45px",
                  },
                  textAlign: "center",
                  cursor: "pointer",
                  "&:hover": {
                    color: "#2932E5",
                  },
                  userSelect: "none",
                }}
              >
                {zoom}%
              </Typography>

              <Tooltip title="Zoom in">
                <span>
                  <IconButton
                    onClick={handleZoomIn}
                    disabled={zoom >= 400}
                    size="small"
                    sx={{
                      color: "#4A5565",
                      "&:hover": {
                        bgcolor: "#E5E7EB",
                      },
                      "&:disabled": {
                        color: "#E5E7EB",
                      },
                    }}
                  >
                    <ZoomInIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>

            {/* Right Section: Actions */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                flex: "0 0 auto",
              }}
            >
              {isImage && (
                <>
                  <Tooltip title="Rotate">
                    <IconButton
                      onClick={handleRotate}
                      size="small"
                      sx={{
                        color: "#4A5565",
                        "&:hover": {
                          bgcolor: "#E5E7EB",
                        },
                      }}
                    >
                      <RotateRightIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}

              <Tooltip title="Fullscreen">
                <IconButton
                  onClick={handleFullscreen}
                  size="small"
                  sx={{
                    color: isFullscreen ? "#2932E5" : "#4A5565",
                    bgcolor: isFullscreen ? "#FFE0D0" : "transparent",
                    "&:hover": {
                      bgcolor: isFullscreen ? "#FFE0D0" : "#E5E7EB",
                    },
                  }}
                >
                  <FullscreenIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Info">
                <IconButton
                  onClick={() => setInfoDrawerOpen(true)}
                  size="small"
                  sx={{
                    color: "#4A5565",
                    "&:hover": {
                      bgcolor: "#E5E7EB",
                    },
                  }}
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )}

        {/* Fullscreen Header Overlay for Non-PDF Files - Show on hover */}
        {!isPDF && isFullscreen && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              zIndex: 1000,
              opacity: 0,
              transition: "opacity 0.3s ease-in-out",
              "&:hover": {
                opacity: 1,
              },
            }}
          >
            <Box
              sx={{
                height: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 1,
                gap: 1,
                borderBottom: "1px solid #E5E7EB",
                bgcolor: "#F2F2F2",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Tooltip title="Close">
                  <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{
                      color: "#4A5565",
                      "&:hover": {
                        bgcolor: "#E5E7EB",
                      },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Typography
                  sx={{
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#212121",
                  }}
                >
                  {file.name}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {isImage && (
                  <Tooltip title="Rotate">
                    <IconButton
                      onClick={handleRotate}
                      size="small"
                      sx={{
                        color: "#4A5565",
                        "&:hover": {
                          bgcolor: "#E5E7EB",
                        },
                      }}
                    >
                      <RotateRightIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Exit Fullscreen">
                  <IconButton
                    onClick={handleFullscreen}
                    size="small"
                    sx={{
                      color: "#2932E5",
                      "&:hover": {
                        bgcolor: "#E5E7EB",
                      },
                    }}
                  >
                    <FullscreenIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        )}

        {/* Content Area */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            bgcolor: "#E5E7EB",
            position: "relative",
            padding: "3px", // Material Design 3: Reduced padding to 3px
          }}
        >
          {/* Loading Spinner */}
          {isLoading && file.fileUrl && (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 10,
              }}
            >
              <CircularProgress
                size={48}
                sx={{
                  color: "#2932E5",
                }}
              />
            </Box>
          )}

          {/* PDF Viewer - Using react-pdf-viewer with enhanced controls */}
          {isPDF && blobUrl && (
            <Box
              ref={pdfContainerRef}
              sx={{
                position: "relative",
                width: "100%",
                height: "100%",
                overflow: "auto",
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start",
                bgcolor: "white",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  height: "100%",
                }}
              >
                <PdfViewer
                  ref={pdfViewerRef}
                  fileUrl={blobUrl}
                  currentPage={currentPage}
                  onPageChange={setCurrentPage}
                  zoom={zoom}
                  onZoomChange={setZoom}
                  rotation={rotation}
                  onDocumentLoad={handleDocumentLoad}
                  style={{
                    height: "100%",
                    width: "100%",
                  }}
                />

                {/* Pins Overlay - positioned relative to PDF dimensions */}
                {pdfPageWidth > 0 && pdfPageHeight > 0 && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      pointerEvents: "none",
                      "& > *": { pointerEvents: "auto" },
                    }}
                  >
                    {pins.map((pin) => (
                      <PDFPinMarker
                        key={pin.id}
                        pin={pin}
                        onDelete={onDeletePin}
                        onUpdateLabel={onUpdatePinLabel}
                        onUpdatePosition={onUpdatePinPosition}
                        onRecordingClick={onRecordingClick}
                        onRecordingPlay={onRecordingPlay}
                        recordings={recordings}
                        isHistorical={
                          viewedSession !== null &&
                          viewedSession !== currentSession
                        }
                        currentSession={currentSession}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* Image Viewer */}
          {isImage && file.fileUrl && (
            <Box
              sx={{
                position: "relative",
                transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
                transformOrigin: "center center",
                transition: "transform 0.2s ease-in-out",
                maxWidth: "100%",
                // Material Design 3: Changed from 90% to 100% (padding handled by parent)
                maxHeight: "100%", // Material Design 3: Changed from 90% to 100% (padding handled by parent)
              }}
            >
              <img
                src={file.fileUrl}
                alt={file.name}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                }}
                onLoad={() => setIsLoading(false)}
                onError={() => {
                  setIsLoading(false);
                  toast.error("Failed to load image");
                }}
              />
            </Box>
          )}

          {/* No File URL */}
          {!file.fileUrl && (
            <Box
              sx={{
                textAlign: "center",
                color: "#4A5565",
              }}
            >
              <Box
                sx={{
                  fontSize: "48px",
                  mb: 2,
                }}
              >
                ⚠️
              </Box>
              <Box
                sx={{
                  fontSize: "16px",
                  fontWeight: 500,
                }}
              >
                File not available
              </Box>
              <Box
                sx={{
                  fontSize: "14px",
                  mt: 1,
                  color: "#6B7280",
                }}
              >
                The file URL is missing or invalid
              </Box>
            </Box>
          )}

          {/* Unsupported File Type */}
          {file.fileUrl && !isPDF && !isImage && (
            <Box
              sx={{
                textAlign: "center",
                color: "#4A5565",
              }}
            >
              <Box
                sx={{
                  fontSize: "48px",
                  mb: 2,
                }}
              >
                📄
              </Box>
              <Box
                sx={{
                  fontSize: "16px",
                  fontWeight: 500,
                }}
              >
                Preview not available for this file type
              </Box>
              <Box
                sx={{
                  fontSize: "14px",
                  mt: 1,
                  color: "#6B7280",
                }}
              >
                {file.fileType || "Unknown file type"}
              </Box>
            </Box>
          )}
        </Box>

        {/* Info Drawer */}
        <ViewerInfoDrawer
          open={infoDrawerOpen}
          onClose={() => setInfoDrawerOpen(false)}
          fileName={file.name}
          fileType={file.fileType || "Unknown"}
          fileSize={file.size || "0 MB"}
          owner={file.owner || "Unknown"}
          dateModified={file.dateModified || "Unknown"}
          description=""
        />
      </Box>
    </Dialog>
  );
}
